import {slotHasWrittenText} from "@/utils/articleAiLock";

export type CompletionLike = {
    value1?: string | null;
    value2?: string | null;
    state?: string;
    seq?: number;
};

export function orderedCompletionValues<T extends {seq?: number}>(
    content: T[] | null | undefined
): T[] {
    return [...(content || [])].sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
}

/**
 * v1.14.2 wrote completion rows as {keyword, value1, value2} without `state`.
 * Every current server write of text sets `state`, so a row with text and no
 * `state` is legacy. An unfilled placeholder has no `state` either, but it has
 * no text to count.
 */
export function isLegacyCompletionRow(row: CompletionLike | null | undefined): boolean {
    return !!row && (row.state === undefined || row.state === null);
}

export function isWritableCompletion(row: CompletionLike | null | undefined): boolean {
    if (!row) {
        return false;
    }
    return slotHasWrittenText(row.value1, row.value2) || row.state === "failed";
}

/**
 * Whole-article jobs often persist an empty placeholder at seq 0 before the
 * real H1/H2 values. Applying by raw index then writes nothing into the
 * editor. Always skip empty docs and keep a single H1.
 */
export function selectWritableCompletions<T extends CompletionLike>(
    content: T[] | null | undefined,
    part: string
): T[] {
    const writable = orderedCompletionValues(content).filter(isWritableCompletion);
    if (part === "h1") {
        return writable.slice(0, 1);
    }
    return writable;
}

export function shouldWaitForWholeHeadingCount(
    mode: string | undefined,
    count: number,
    part: string
): boolean {
    return mode === "whole" && (part === "h1" || part === "h2") && !(count > 0);
}

export function planWholeArticleWrites<T extends CompletionLike>(
    completionsByPart: Partial<Record<string, T[] | null | undefined>>
): Array<{part: string; index: number}> {
    const plan: Array<{part: string; index: number}> = [];
    for (const part of ["h1", "h2", "h3", "h4", "h5", "h6"]) {
        const values = selectWritableCompletions(completionsByPart[part], part);
        for (let index = 0; index < values.length; index++) {
            plan.push({part, index});
        }
    }
    return plan;
}
